/**
 * @file UI 状态推导（deriveUi）—— 把 `ClientViewState` 变成渲染层可用的
 * `TableUiState`。纯函数、两种模式共用、只消费公开可见信息。
 *
 * ## 信息隔离
 * 只使用 `SeatView`（自家手牌 / 他家计数 / 公开事件）。自摸可胡、自家可杠
 * 均由**自家可见信息**推导（引擎/服务端最终校验），不触碰墙序与他家手牌。
 */

import type { ClaimActionType, SeatIndex, SeatView, Suit, TileId, TileKind } from '@majiang/core';
import { checkWin, kindOfTileId } from '@majiang/core';
import type { ClientViewState, TableUiState } from './types';

/** 由视图状态推导渲染用 UI 状态。 */
export function deriveUi(state: ClientViewState, toast: string | null = null): TableUiState {
  const view = state.view;
  if (view === null) {
    return emptyUi('连接中…', toast);
  }
  const self = view.self;
  const phase = view.phase;
  const myTurn = phase === 'playing' && !self.hasWon && view.turn === view.viewerSeat && state.claimWindow === null;

  const needsVoid = phase === 'void' && self.voidSuit === null;
  const claims: readonly ClaimActionType[] = state.myClaim?.actions ?? [];
  const gangKinds: readonly TileKind[] = state.myClaim?.gangKinds ?? [];
  const selfGangKinds = myTurn ? computeSelfGangKinds(view) : [];
  const canSelfGang = myTurn && selfGangKinds.length > 0;
  const canZimoHu = myTurn && zimoWinningTileId(view) !== null;

  let phaseLabel: string;
  switch (phase) {
    case 'init':
    case 'dealing':
      phaseLabel = '发牌中…';
      break;
    case 'swap':
      phaseLabel = '换三张…';
      break;
    case 'void':
      phaseLabel = needsVoid ? '请选择定缺花色' : '等待其他玩家定缺…';
      break;
    case 'playing':
      if (self.hasWon) phaseLabel = '你已胡牌，旁观中';
      else if (state.claimWindow !== null) phaseLabel = '等待响应…';
      else if (view.turn === view.viewerSeat) phaseLabel = '轮到你出牌';
      else phaseLabel = `等待 ${seatName(view, view.turn)} 出牌`;
      break;
    case 'settlement':
      phaseLabel = '本局结算';
      break;
    case 'finished':
      phaseLabel = '对局结束';
      break;
    default:
      phaseLabel = '';
  }

  return {
    phaseLabel,
    canDiscard: myTurn,
    discardable: myTurn ? discardableOf(view) : [],
    claims,
    gangKinds,
    canZimoHu,
    canSelfGang,
    selfGangKinds,
    needsVoid,
    toast,
  };
}

function emptyUi(phaseLabel: string, toast: string | null): TableUiState {
  return {
    phaseLabel,
    canDiscard: false,
    discardable: [],
    claims: [],
    gangKinds: [],
    canZimoHu: false,
    canSelfGang: false,
    selfGangKinds: [],
    needsVoid: false,
    toast,
  };
}

/** QC-1：仍持缺门牌 → 只能打缺门牌；否则全部可出。 */
function discardableOf(view: SeatView): readonly TileId[] {
  const suit = view.self.voidSuit;
  if (suit !== null) {
    const queTiles = view.self.hand.filter((id) => kindOfTileId(id).startsWith(suit));
    if (queTiles.length > 0) return queTiles;
  }
  return view.self.hand;
}

/** 自家回合可杠的牌面：暗杠（手中 4 张）+ 巴杠（已碰 + 第 4 张）。 */
function computeSelfGangKinds(view: SeatView): TileKind[] {
  const self = view.self;
  const suitOf = (kind: TileKind): Suit => kind.slice(0, -1) as Suit;
  const que = self.voidSuit;
  const counts = new Map<string, number>();
  for (const id of self.hand) {
    const kind = kindOfTileId(id);
    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  const out: TileKind[] = [];
  for (const [kind, n] of counts) {
    if (que !== null && suitOf(kind as TileKind) === que) continue; // QC-5：缺门牌不得成杠
    if (n === 4) out.push(kind as TileKind);
  }
  for (const m of self.melds) {
    if (m.type !== 'peng') continue;
    if (que !== null && suitOf(m.kind) === que) continue;
    if ((counts.get(m.kind) ?? 0) >= 1) out.push(m.kind);
  }
  return out;
}

/**
 * 自摸可胡：把手里任意一张当作「刚摸到的和了张」，若某张移除后余牌仍成胡
 * 即认为可自摸胡。
 *
 * 口径与引擎 `zimoWinningTile`（game.ts）一致：手牌升序遍历，返回**第一个**
 * 移除后仍听该牌面的 id —— 联机模式服务端据此校验，客户端必须复算同一 id。
 */
export function zimoWinningTileId(view: SeatView): TileId | null {
  const self = view.self;
  if (self.hand.length === 0) return null;
  const hand = [...self.hand].sort((a, b) => a - b);
  for (const id of hand) {
    const handWithout = hand.filter((x) => x !== id);
    const res = checkWin({
      hand: handWithout,
      melds: self.melds,
      winningTile: id,
      winKind: 'zimo',
      voidSuit: self.voidSuit,
      rules: view.rules,
      isLastTile: view.wallRemaining === 0,
      gen: null,
    });
    if (res.isWin) return id;
  }
  return null;
}

function seatName(view: SeatView, seat: SeatIndex): string {
  if (seat === view.viewerSeat) return '你';
  const o = view.opponents.find((x) => x.seat === seat);
  if (o === undefined) return `座位${seat + 1}`;
  return o.playerId.replace(/^bot:/, 'AI·').slice(0, 8);
}
